import React, { useState } from 'react';
import { Question } from "@/types/test";
import QuestionEditor from "./QuestionEditor";
import { QuestionCardView } from "./QuestionCardView";
import { useIsMobile } from "@/hooks/use-mobile";
import { validateQuestion } from "./utils/questionValidation";
import { Button } from "@/components/ui/button";
import { MarkschemeDialog } from "@/components/shared/MarkschemeDialog";

interface QuestionListProps {
  questions: Question[];
  onAddQuestion: () => string; // Updated to return the new question ID
  onUpdateQuestion: (id: string, updates: Partial<Question>) => void;
  onDeleteQuestion: (id: string) => void;
  onRegenerateQuestion?: (id: string, feedback: string) => Promise<void>;
  jobContext?: {
    title?: string;
    description?: string;
    keySkills?: string[];
    yearsOfExperience?: string;
  };
}

export const QuestionList = React.memo(({
  questions,
  onAddQuestion,
  onUpdateQuestion,
  onDeleteQuestion,
  onRegenerateQuestion,
  jobContext,
}: QuestionListProps) => {
  console.log('Rendering QuestionList with', questions.length, 'questions');
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(null);
  const [markschemeDialogOpen, setMarkschemeDialogOpen] = useState(false);
  const [selectedQuestionForMarkscheme, setSelectedQuestionForMarkscheme] = useState<Question | null>(null);
  const [lastQuestionCount, setLastQuestionCount] = useState(questions.length);
  const isMobile = useIsMobile();

  // Auto-edit newly added questions
  React.useEffect(() => {
    if (questions.length > lastQuestionCount) {
      // A new question was added, set it as editing
      const newQuestion = questions[questions.length - 1];
      setEditingQuestionId(newQuestion.id);
    }
    setLastQuestionCount(questions.length);
  }, [questions.length, lastQuestionCount, questions]);

  const handleEditQuestion = (questionId: string) => {
    setEditingQuestionId(questionId);
  };

  const handleDoneEditing = () => {
    setEditingQuestionId(null);
  };

  const handleMarkscheme = (question: Question, questionNumber: number) => {
    setSelectedQuestionForMarkscheme(question);
    setMarkschemeDialogOpen(true);
  };

  const handleMarkschemeUpdate = (markscheme: any) => {
    if (selectedQuestionForMarkscheme) {
      onUpdateQuestion(selectedQuestionForMarkscheme.id, { 
        markscheme, 
        marks: markscheme.total_points || 1
      });
    }
    setMarkschemeDialogOpen(false);
    setSelectedQuestionForMarkscheme(null);
  };
  
  return (
    <div className="space-y-8 text-foreground">
      {questions.length === 0 ? (
        <div className="text-center p-10 bg-card rounded-md border border-border">
          <p className="text-muted-foreground">No questions added yet. Add questions using the buttons above.</p>
        </div>
      ) : (
        questions.map((question, index) => (
          <div key={question.id} className="space-y-4">
            {editingQuestionId === question.id ? (
              <QuestionEditor
                question={question}
                questionNumber={index + 1}
                onUpdate={onUpdateQuestion}
                onDelete={onDeleteQuestion}
                onRegenerate={onRegenerateQuestion}
                onDoneEditing={handleDoneEditing}
                jobContext={jobContext}
              />
            ) : (
              <QuestionCardView
                question={question}
                questionNumber={index + 1}
                onEdit={() => handleEditQuestion(question.id)}
                onDelete={onDeleteQuestion}
                onRegenerate={onRegenerateQuestion}
                onMarkscheme={() => handleMarkscheme(question, index + 1)}
              />
            )}
          </div>
        ))
      )}

      {/* Markscheme Dialog */}
      {selectedQuestionForMarkscheme && (
        <MarkschemeDialog
          questionNumber={questions.findIndex(q => q.id === selectedQuestionForMarkscheme.id) + 1}
          markscheme={selectedQuestionForMarkscheme.markscheme}
          open={markschemeDialogOpen}
          onOpenChange={setMarkschemeDialogOpen}
          onSave={handleMarkschemeUpdate}
          question={selectedQuestionForMarkscheme}
          jobContext={jobContext}
        />
      )}
    </div>
  );
});

QuestionList.displayName = 'QuestionList';